import { UISref } from "@uirouter/react";
import { noop } from "lodash";
import { FunctionComponent, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { EditableField } from "../../../components/forms/field";
import { useAppLoader } from "../../../components/loader/app-loader-hook";
import { Flex } from "../../../components/layout";
import { Loader } from "../../../components/loader";
import { AuthActions } from "../../../store/auth/actions";
import { AuthDTO } from "../../../store/auth/models/dto";
import { Auth } from "../../../store/auth/reducer";
import { AuthService } from "../../../store/auth/service";
import { useFetchable } from "../../../store/common/fetchable";
import { useYupValidationResolver } from "../../../utils/utils";
import { Form } from "../base";
import { triggerEvent } from "../../../store/eventstore/trigger-event";
import { FetchableSuccess } from "../../../store/common/fetchable/helpers";

type RequestDTO = AuthDTO.Requests.TwoFactorRequest.T;
const { useFields } = AuthDTO.Requests.TwoFactorRequest;

const Container = styled(Flex)`
  flex-direction: column;
`;

const PresentationalEnterOTPPage: FunctionComponent<{
  login: AuthDTO.Responses.Login.T;
}> = ({ login }) => {
  const { labels, validation } = useFields();
  const resolver = useYupValidationResolver(validation);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<RequestDTO>({
    defaultValues: {
      provider: AuthDTO.Responses.Login.isWithTwoFA(login)
        ? login.twoFactorAuth.provider
        : undefined,
      password: "",
    },
    resolver,
  });

  const dispatch = useDispatch();
  const loadable = useAppLoader();

  const onSubmit = useCallback(
    (data: AuthDTO.Requests.TwoFactorRequest.T) => {
      reset();
      return loadable(
        AuthService.postTwoFactorRequest(data)
          .then(
            (data) =>
              ({
                status: "ready",
                data,
              } as FetchableSuccess<AuthDTO.Responses.Login.T>)
          )
          .then(triggerEvent("AUTH_DONE"))
          .then((data) => dispatch(AuthActions.authDone(data)))
          .catch(noop)
      );
    },
    [dispatch]
  );

  const pass = watch("password");
  if (pass.length >= 6 && AuthDTO.Responses.Login.isWithTwoFA(login)) {
    onSubmit({
      password: pass,
      provider: login.twoFactorAuth.provider,
    });
  }
  return (
    <Container>
      <h1 className="h1">Enter OTP code</h1>
      <Form.Form onSubmit={handleSubmit((data) => onSubmit(data))}>
        <EditableField editing control={control} name="password">
          <Form.TextField
            fullWidth
            type="text"
            label={labels.password}
            error={!!errors.password}
            helperText={errors.password?.message}
          />
        </EditableField>
      </Form.Form>
      <Form.Smaller>
        <UISref to="^.2fa_qr">
          <a>I don't have Google Authenticator set up</a>
        </UISref>
      </Form.Smaller>
      <Form.Smaller>
        <UISref to="^.login">
          <a>Enter credentials</a>
        </UISref>
      </Form.Smaller>
    </Container>
  );
};

export const EnterOTPPage = () => {
  const login = useFetchable(Auth.login, { username: "", password: "" }); // TODO makes no sense to default this fetchable
  return (
    <Loader data={[login]}>
      {([data]) => <PresentationalEnterOTPPage login={data} />}
    </Loader>
  );
};
